import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Truck, Phone, Lock, ArrowLeft, CheckCircle, Eye, EyeOff } from 'lucide-react';
import { API_ENDPOINTS } from '../config/api';
import logger from '../utils/logger';

const DriverPasswordResetPage = () => {
  const [step, setStep] = useState(1); // 1: phone, 2: code, 3: new password
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch
  } = useForm();

  const newPassword = watch('newPassword', '');

  // Step 1: Request password reset (send SMS)
  const onRequestReset = async (data) => {
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${API_ENDPOINTS.AUTH}/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ phone: data.phone })
      });

      const result = await response.json();
      
      if (result.success) {
        setPhoneNumber(data.phone);
        setStep(2);
        toast.success('קוד אימות נשלח למספר הטלפון שלך');
      } else {
        toast.error(result.message || 'שגיאה בשליחת קוד האימות');
      }
    } catch (error) {
      logger.error('Password reset request error:', error);
      toast.error('שגיאה בשליחת קוד האימות');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Step 2: Verify SMS code
  const onVerifyCode = async (data) => {
    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${API_ENDPOINTS.AUTH}/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phone: phoneNumber,
          code: data.code,
          newPassword: data.newPassword
        })
      });

      const result = await response.json();
      
      if (result.success) {
        setStep(3);
        toast.success('הסיסמה שונתה בהצלחה!');
      } else {
        toast.error(result.message || 'שגיאה באיפוס הסיסמה');
      }
    } catch (error) {
      logger.error('Password reset error:', error);
      toast.error('שגיאה באיפוס הסיסמה');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStep1 = () => (
    <form className="space-y-6" onSubmit={handleSubmit(onRequestReset)}>
      <div>
        <label htmlFor="reset-phone" className="form-label flex items-center">
          <div className="bg-orange-100 p-2 rounded-full ml-3">
            <Phone className="h-4 w-4 text-orange-600" />
          </div>
          מספר טלפון
        </label>
        <input
          id="reset-phone"
          type="tel"
          {...register('phone', {
            required: 'מספר הטלפון הוא שדה חובה',
            pattern: {
              value: /^05\d{8}$/,
              message: 'מספר הטלפון חייב להיות בפורמט תקין (05XXXXXXXX)'
            }
          })}
          maxLength={11}
          className="input-field"
          placeholder="05XXXXXXXX"
        />
        {errors.phone && (
          <p className="form-error">{errors.phone.message}</p>
        )}
      </div>

      <div>
        <button
          type="submit"
          disabled={isSubmitting}
          className="btn-primary w-full py-4 text-lg disabled:opacity-50 disabled:cursor-not-allowed hover-lift"
        >
          {isSubmitting ? (
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white ml-2"></div>
              שולח קוד...
            </div>
          ) : (
            'שלח קוד אימות'
          )}
        </button>
      </div>
    </form>
  );

  const renderStep2 = () => (
    <form className="space-y-6" onSubmit={handleSubmit(onVerifyCode)}>
      <div>
        <label htmlFor="reset-code" className="form-label flex items-center">
          <div className="bg-orange-100 p-2 rounded-full ml-3">
            <Phone className="h-4 w-4 text-orange-600" />
          </div>
          קוד אימות
        </label>
        <input
          id="reset-code"
          type="text"
          {...register('code', {
            required: 'קוד האימות הוא שדה חובה',
            pattern: {
              value: /^\d{4,6}$/,
              message: 'קוד האימות חייב להכיל 4-6 ספרות'
            }
          })}
          maxLength={6}
          className="input-field text-center text-2xl tracking-widest"
          placeholder="000000"
        />
        {errors.code && (
          <p className="form-error">{errors.code.message}</p>
        )}
        <p className="text-sm text-brown-600 mt-2">
          קוד האימות נשלח למספר: {phoneNumber}
        </p>
      </div>

      <div>
        <label htmlFor="reset-new-password" className="form-label flex items-center">
          <div className="bg-orange-100 p-2 rounded-full ml-3">
            <Lock className="h-4 w-4 text-orange-600" />
          </div>
          סיסמה חדשה
        </label>
        <div className="relative">
          <input
            id="reset-new-password"
            type={showPassword ? 'text' : 'password'}
            {...register('newPassword', {
              required: 'הסיסמה החדשה היא שדה חובה',
              minLength: {
                value: 10,
                message: 'הסיסמה חייבת להכיל לפחות 10 תווים'
              },
              maxLength: {
                value: 20,
                message: 'הסיסמה לא יכולה להכיל יותר מ-20 תווים'
              }
            })}
            maxLength={20}
            className="input-field pr-12"
            placeholder="הזן סיסמה חדשה"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-brown-500 hover:text-brown-700"
          >
            {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
          </button>
        </div>
        {errors.newPassword && (
          <p className="form-error">{errors.newPassword.message}</p>
        )}
        {newPassword && (
          <div className="mt-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-brown-700">עוצמת הסיסמה:</span>
              <span className={`text-sm font-semibold ${
                newPassword.length < 10 ? 'text-red-600' : 
                newPassword.length < 15 ? 'text-orange-600' : 
                'text-green-600'
              }`}>
                {newPassword.length < 10 ? 'חלשה' : 
                 newPassword.length < 15 ? 'בינונית' : 'חזקה'}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full transition-all duration-300 ${
                  newPassword.length < 10 ? 'bg-red-500' : 
                  newPassword.length < 15 ? 'bg-orange-500' : 'bg-green-500'
                }`}
                style={{ width: `${Math.min(100, (newPassword.length / 20) * 100)}%` }}
              />
            </div>
            <div className="text-sm text-brown-600">
              {newPassword.length < 10 && 'הסיסמה חייבת להכיל לפחות 10 תווים'}
              {newPassword.length >= 10 && newPassword.length < 15 && 'הסיסמה טובה, אבל תוכל לשפר אותה'}
              {newPassword.length >= 15 && 'הסיסמה חזקה!'}
            </div>
          </div>
        )}
      </div>

      <div>
        <label htmlFor="reset-confirm-password" className="form-label flex items-center">
          <div className="bg-orange-100 p-2 rounded-full ml-3">
            <Lock className="h-4 w-4 text-orange-600" />
          </div>
          חזור על הסיסמה
        </label>
        <div className="relative">
          <input
            id="reset-confirm-password"
            type={showConfirmPassword ? 'text' : 'password'}
            {...register('confirmPassword', {
              required: 'אישור הסיסמה הוא שדה חובה',
              validate: (value) => value === newPassword || 'הסיסמאות אינן תואמות'
            })}
            maxLength={20}
            className="input-field pr-12"
            placeholder="הזן שוב את הסיסמה"
          />
          <button
            type="button"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-brown-500 hover:text-brown-700"
          >
            {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
          </button>
        </div>
        {errors.confirmPassword && (
          <p className="form-error">{errors.confirmPassword.message}</p>
        )}
      </div>

      <div>
        <button
          type="submit"
          disabled={isSubmitting}
          className="btn-primary w-full py-4 text-lg disabled:opacity-50 disabled:cursor-not-allowed hover-lift"
        >
          {isSubmitting ? (
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white ml-2"></div>
              מאפס סיסמה...
            </div>
          ) : (
            'אפס סיסמה'
          )}
        </button>
      </div>

      <div className="text-center">
        <button
          type="button"
          onClick={() => setStep(1)}
          className="text-orange-600 hover:text-orange-700 font-semibold underline"
        >
          חזור לשלב הקודם
        </button>
      </div>
    </form>
  );

  const renderStep3 = () => (
    <div className="text-center space-y-6">
      <div className="flex justify-center">
        <div className="bg-green-100 p-4 rounded-full">
          <CheckCircle className="h-16 w-16 text-green-600" />
        </div>
      </div>
      
      <div>
        <h3 className="text-2xl font-bold text-green-600 mb-2">
          הסיסמה שונתה בהצלחה!
        </h3>
        <p className="text-brown-700">
          הסיסמה שלך שונתה בהצלחה. כעת תוכל להתחבר עם הסיסמה החדשה.
        </p>
      </div>

      <div>
        <button
          onClick={() => navigate('/driver/login')}
          className="btn-primary w-full py-4 text-lg hover-lift"
        >
          התחבר עכשיו
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-brown-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Skip link targets */}
        <div id="main-content" className="sr-only" tabIndex="-1">תוכן ראשי</div>
        {/* Header */}
        <div id="navigation" className="text-center" tabIndex="-1">
          <button
            onClick={() => navigate('/driver/login')}
            className="flex items-center text-orange-600 hover:text-orange-700 mb-6 mx-auto text-lg font-semibold transition-colors duration-300"
          >
            <ArrowLeft className="h-5 w-5 ml-3" />
            חזור להתחברות
          </button>
          
          <div className="flex justify-center mb-6">
            <div className="bg-orange-100 p-4 rounded-full">
              <Truck className="h-16 w-16 text-orange-600" />
            </div>
          </div>
          <h2 className="text-4xl font-bold text-brown-900 mb-4">
            איפוס סיסמה
          </h2>
          <p className="text-lg text-brown-700">
            {step === 1 && 'הזן את מספר הטלפון שלך כדי לקבל קוד אימות'}
            {step === 2 && 'הזן את קוד האימות והסיסמה החדשה'}
            {step === 3 && 'הסיסמה שלך שונתה בהצלחה'}
          </p>
        </div>

        {/* Progress Steps */}
        <div className="flex justify-center mb-8">
          <div className="flex items-center space-x-4 space-x-reverse">
            {[1, 2, 3].map((stepNumber) => (
              <div key={stepNumber} className="flex items-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                    step >= stepNumber
                      ? 'bg-orange-600 text-white'
                      : 'bg-gray-300 text-gray-600'
                  }`}
                >
                  {stepNumber}
                </div>
                {stepNumber < 3 && (
                  <div
                    className={`w-8 h-1 mx-2 ${
                      step > stepNumber ? 'bg-orange-600' : 'bg-gray-300'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Form */}
        <div className="card hover-lift">
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
        </div>

        {/* Footer */}
        <div className="text-center">
          <p className="text-brown-600">
            זוכר את הסיסמה?{' '}
            <Link
              to="/driver/login"
              className="text-orange-600 hover:text-orange-700 font-semibold underline"
            >
              התחבר עכשיו
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default DriverPasswordResetPage;
